import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiClock, FiCheckCircle, FiXCircle, FiEye, FiEdit2, FiRefreshCw, FiInfo } from 'react-icons/fi';
import { datosAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import storageService from '../services/storageService';

/**
 * Página para que el usuario vea sus datos históricos enviados y su estado en el flujo
 */
export default function MisDatosHistoricos() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [datosEnviados, setDatosEnviados] = useState([]);
  const [datosLocales, setDatosLocales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('enviados'); // 'enviados' o 'locales'

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    cargarDatos();
  }, [user, navigate]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      // Cargar datos enviados desde el backend
      const response = await datosAPI.obtenerHistorialGeneral({
        usuarioId: user.id,
        limit: 100
      });
      const datosFiltrados = (response.data.datos || []).filter(
        d => d.usuarioInformanteId === user.id
      );
      setDatosEnviados(datosFiltrados);

      // Cargar datos locales desde IndexedDB
      await storageService.init();
      const locals = await storageService.historicalData.obtenerTodos();
      setDatosLocales(locals);
    } catch (error) {
      console.error('Error cargando datos:', error);
      setDatosEnviados([]);
      setDatosLocales([]);
    } finally {
      setLoading(false);
    }
  };

  const getEstadoInfo = (estado) => {
    const estados = {
      'borrador_usuario': { 
        label: 'Borrador', 
        color: 'gray',
        icon: FiEdit2,
        desc: 'En tu bandeja personal'
      },
      'borrador_local': { 
        label: 'Local', 
        color: 'yellow',
        icon: FiClock,
        desc: 'Guardado en tu navegador'
      },
      'creada_gestor': { 
        label: 'En Revisión (Gestor)', 
        color: 'blue',
        icon: FiClock,
        desc: 'Esperando revisión del gestor'
      },
      'informacion_valida': { 
        label: 'Validado', 
        color: 'indigo',
        icon: FiCheckCircle,
        desc: 'Aprobado por el validador'
      },
      'informacion_contrastada_editor': { 
        label: 'Editado', 
        color: 'purple',
        icon: FiCheckCircle,
        desc: 'Procesado por el editor'
      },
      'informacion_aprobada': { 
        label: 'Publicado', 
        color: 'green',
        icon: FiCheckCircle,
        desc: 'Visible en la plataforma'
      },
      'rechazada': { 
        label: 'Rechazado', 
        color: 'red',
        icon: FiXCircle,
        desc: 'No cumplió los criterios'
      }
    };
    return estados[estado] || { label: estado, color: 'gray', icon: FiInfo, desc: 'Estado desconocido' };
  };

  const getColorClasses = (color) => {
    const colors = {
      gray: 'bg-gray-100 text-gray-700 border-gray-300',
      yellow: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      blue: 'bg-blue-100 text-blue-700 border-blue-300',
      indigo: 'bg-indigo-100 text-indigo-700 border-indigo-300',
      purple: 'bg-purple-100 text-purple-700 border-purple-300',
      green: 'bg-green-100 text-green-700 border-green-300',
      red: 'bg-red-100 text-red-700 border-red-300'
    };
    return colors[color] || colors.gray;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold mb-2">Mis Datos Históricos</h1>
          <p className="text-purple-100">
            Seguimiento de tus contribuciones y su estado en el proceso de publicación
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('enviados')}
                className={`px-6 py-4 border-b-2 font-medium text-sm ${
                  activeTab === 'enviados'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📤 Enviados ({datosEnviados.length})
              </button>
              <button
                onClick={() => setActiveTab('locales')}
                className={`px-6 py-4 border-b-2 font-medium text-sm ${
                  activeTab === 'locales'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                💾 Locales ({datosLocales.length})
              </button>
            </nav>
          </div>
        </div>

        {/* Contenido según tab */}
        {activeTab === 'enviados' ? (
          <div>
            {datosEnviados.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <FiInfo className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-700 mb-2">
                  No has enviado datos aún
                </h2>
                <p className="text-gray-500 mb-6">
                  Los datos que envíes a revisión aparecerán aquí
                </p>
                <button
                  onClick={() => navigate('/historicidad/crear')}
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Crear Primer Dato
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {datosEnviados.map((dato) => {
                  const estadoInfo = getEstadoInfo(dato.estado);
                  const Icon = estadoInfo.icon;
                  
                  return (
                    <div
                      key={dato.id}
                      className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">
                            {dato.tema}
                          </h3>
                          <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                            {dato.descripcion}
                          </p>

                          <div className="flex flex-wrap gap-3 mb-4">
                            {/* Estado */}
                            <span
                              className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium border ${getColorClasses(estadoInfo.color)}`}
                            >
                              <Icon className="w-4 h-4" />
                              {estadoInfo.label}
                            </span>

                            {/* Fecha de envío */}
                            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm text-gray-600 bg-gray-100">
                              <FiClock className="w-4 h-4" />
                              Enviado: {formatDate(dato.createdAt)}
                            </span>
                          </div>

                          <p className="text-xs text-gray-500">
                            {estadoInfo.desc}
                          </p>
                        </div>

                        <div className="ml-4">
                          <Link
                            to={`/historicidad/${dato.slug}`}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                          >
                            <FiEye className="w-4 h-4" />
                            Ver
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div>
            {datosLocales.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <FiInfo className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-700 mb-2">
                  No tienes datos locales
                </h2>
                <p className="text-gray-500 mb-6">
                  Los datos guardados en tu navegador aparecerán aquí
                </p>
                <button
                  onClick={() => navigate('/historicidad/crear')}
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Crear Dato Local
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {datosLocales.map((dato) => (
                  <div
                    key={dato.id}
                    className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                          {dato.tema}
                          <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full font-medium">
                            💾 Local
                          </span>
                        </h3>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                          {dato.descripcion}
                        </p>

                        <div className="flex flex-wrap gap-3">
                          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm text-gray-600 bg-gray-100">
                            <FiClock className="w-4 h-4" />
                            Creado: {formatDate(dato.createdAt)}
                          </span>
                          {dato.updatedAt && dato.updatedAt !== dato.createdAt && (
                            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm text-gray-600 bg-gray-100">
                              <FiRefreshCw className="w-4 h-4" />
                              Actualizado: {formatDate(dato.updatedAt)}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="ml-4 flex gap-2">
                        <button
                          onClick={() => navigate(`/historicidad/editar/local_${dato.id}`)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                        >
                          <FiEdit2 className="w-4 h-4" />
                          Editar
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Botón para volver */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/historicidad')}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            ← Volver a Historicidad
          </button>
        </div>
      </div>
    </div>
  );
}



